import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';

import { TabNavigationLayout } from '../../templates/TabNavigationLayout';
import { PageWrapper } from '../../atoms/PageWrapper';
import { EmptyBlock } from '../../molecules/EmptyBlock';
import { Loader } from '../../molecules/Loader';
import { ProgramSampleCard } from '../../organisms/ProgramSampleCard';
import { Breadcrumb } from '../../organisms/Breadcrumb';

import { useUser } from '../../../redux/user/useUser';
import { useGetProgramSamplesQuery } from '../../../gen/graphql';
import { FETCH_LIMIT_HIGH, FIRST_PAGE } from '../../../const/Page';
import { FunctionType, PermissionType } from '../../../const/UserPermission';
import { LOWER_META_TITLE, SERVICE_NAME } from '../../../const/Service';
import { PROGRAM_COUNT } from '../../../const/Program';

type EqualizeTarget = 'title' | 'description';

// 表示上の「同じ行」ごとに要素をグルーピングする
export const groupElementsByRow = (containerRect: DOMRect, elements: HTMLElement[]): Map<number, HTMLElement[]> => {
  const groups = new Map<number, HTMLElement[]>();

  for (const el of elements) {
    const rect = el.getBoundingClientRect();
    // container 上端からの相対 top を「同じ行判定のキー」として算出（微小なズレは丸めて吸収）
    const key = Math.round(rect.top - containerRect.top);

    const current = groups.get(key);
    if (current) current.push(el);
    else groups.set(key, [el]);
  }

  return groups;
};

// 1行（グループ）内の要素のうち、最大の高さを計算する
export const computeMaxHeight = (elements: HTMLElement[]): number => {
  let maxHeight = 0;
  for (const el of elements) {
    maxHeight = Math.max(maxHeight, el.getBoundingClientRect().height);
  }
  return maxHeight;
};

// data-equalize="target" の要素を「行ごと」に最大高さへ揃える
export const equalizeHeightsByRow = (container: HTMLElement, target: EqualizeTarget): void => {
  // container 配下から data-equalize 属性を持つ要素（title/description）だけを取得する
  const elements = Array.from(container.querySelectorAll<HTMLElement>(`[data-equalize="${target}"]`));
  if (elements.length === 0) return;

  // 前回の固定 height を解除して、最新の実寸を計測できる状態に戻す
  for (const el of elements) {
    el.style.height = '';
  }

  const containerRect = container.getBoundingClientRect();
  const groups = groupElementsByRow(containerRect, elements);

  for (const groupEls of groups.values()) {
    const maxHeight = computeMaxHeight(groupEls);
    // 1行（グループ）内の全要素に、最大高さを固定で適用する
    for (const el of groupEls) {
      el.style.height = `${maxHeight}px`;
    }
  }
};

export const ProgramSample: React.FC = () => {
  const metaTitle = `成果物サンプル一覧 | ${LOWER_META_TITLE}`;
  const metaDescription = `${SERVICE_NAME}のプログラミング学習教材ページです。ITリテラシーからフロントエンド、バックエンド、インフラまで全${PROGRAM_COUNT}種類以上の教材をご用意。入門者や初心者にも優しい基礎教材はもちろん実務レベルのエンジニアになるための中級者向け教材も。`;

  const { permissionCheck } = useUser();
  const query = queryString.parse(useLocation().search);

  const tabs = useMemo(() => {
    const tabs = [];

    permissionCheck(FunctionType.ProgramForInstructorAndCoach, PermissionType.Read)
      ? tabs.push(
          { label: '学習用教材', to: '/programs?type=1', active: query.type === '1' },
          {
            label: 'インストラクター向けマニュアル',
            to: '/programs?type=2',
            active: query.type === '2',
          },
        )
      : tabs.push(
          { label: '教材TOP', to: '/programs', active: location.pathname === '/programs' },
          {
            label: '学習中の教材',
            to: '/programs_while_studying',
            active: location.pathname === '/programs_while_studying',
          },
          {
            label: '完了した教材',
            to: '/programs_completed_study',
            active: location.pathname === '/programs_completed_study',
          },
        );

    return tabs;
  }, [permissionCheck, query.type]);

  const breadcrumbs = [
    {
      label: 'ホーム',
      to: '/home',
    },
    {
      label: 'サンプル一覧',
    },
  ];

  const { data, error, loading } = useGetProgramSamplesQuery({
    variables: {
      input: {
        limit: FETCH_LIMIT_HIGH,
        page: FIRST_PAGE,
      },
    },
  });
  const samples = useMemo(() => data?.getProgramSamples?.items ?? [], [data?.getProgramSamples?.items]);

  // ProgramSampleCard 内の Title/Description を「同じ行の中で最大の高さ」に揃えるための参照
  const sampleListRef = useRef<HTMLDivElement | null>(null);

  const equalizeHeightsInSampleListByRow = useCallback((target: EqualizeTarget) => {
    const container = sampleListRef.current;
    if (!container) return;
    equalizeHeightsByRow(container, target);
  }, []);

  const runEqualize = useCallback(() => {
    // 先に Title を揃えた後、レイアウトが確定した次フレームで Description を再計測して揃える
    equalizeHeightsInSampleListByRow('title');
    requestAnimationFrame(() => {
      equalizeHeightsInSampleListByRow('description');
    });
  }, [equalizeHeightsInSampleListByRow]);

  const scheduledRafIdRef = useRef<number | null>(null);
  const scheduleEqualize = useCallback(() => {
    if (scheduledRafIdRef.current !== null) {
      cancelAnimationFrame(scheduledRafIdRef.current);
    }

    scheduledRafIdRef.current = requestAnimationFrame(() => {
      scheduledRafIdRef.current = null;
      runEqualize();
    });
  }, [runEqualize]);

  useLayoutEffect(() => {
    if (loading) return;

    const container = sampleListRef.current;
    if (!container) return;

    // 初回描画後も同期実行せず、requestAnimationFrameで実行する
    scheduleEqualize();

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      // コンテナの幅変更（列数・折り返し行の変化）に追従して再計測する
      observer = new ResizeObserver(() => {
        scheduleEqualize();
      });
      observer.observe(container);
    }

    // 念のため window リサイズでも再計測する
    window.addEventListener('resize', scheduleEqualize);
    return () => {
      if (scheduledRafIdRef.current !== null) {
        cancelAnimationFrame(scheduledRafIdRef.current);
        scheduledRafIdRef.current = null;
      }
      observer?.disconnect();
      window.removeEventListener('resize', scheduleEqualize);
    };
  }, [loading, samples, scheduleEqualize]);

  const showLoader = loading;

  return (
    <>
      <Loader display={showLoader} />
      <TabNavigationLayout pageTitle="教材" tabs={tabs} metaTitle={metaTitle} metaDescription={metaDescription}>
        <PageWrapper>
          <Breadcrumb links={breadcrumbs} />
          <PageTitle>サンプル一覧</PageTitle>
          <PageDescription>
            侍テラコヤの教材を通して制作・開発できるサンプルサイトやサンプルアプリをまとめたページです。それぞれの教材の概要にも飛べます。
          </PageDescription>
          {error ? (
            <EmptyBlock title="サンプル一覧の読み込みが行われませんでした">
              回線状況を確認して再読み込みを行ってください
            </EmptyBlock>
          ) : samples.length === 0 ? (
            <EmptyBlock title="現在サンプル一覧に表示させる情報がありません" />
          ) : (
            <SampleList ref={sampleListRef}>
              {samples.map((item) => (
                <ProgramSampleCard key={item.id} sample={item} />
              ))}
            </SampleList>
          )}
        </PageWrapper>
      </TabNavigationLayout>
    </>
  );
};

const PageTitle = styled.h2`
  margin-top: 2rem;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.75rem;
`;
const PageDescription = styled.p`
  margin: 0.5rem auto 1.5rem;
  font-size: 0.875rem;
  line-height: 1.25rem;

  ${media.lessThan('medium')`
    margin-top: .75rem;
  `}
`;
const SampleList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2rem 1.25rem;

  ${media.lessThan('medium')`
    gap: 2rem 1rem;
  `}
`;
